import React, { useEffect, useMemo } from 'react';
import AccountDashboardWidgetBase from "./AccountDashboardWidgetBase";
import { Routes } from '../../../routes/routes';
import {useTranslation} from 'react-i18next';
import { useStyles } from './AccountDashboardPlanWidget.utils';
import { ReactComponent as Plan } from '../../../assets/accountDashboard/Plan.svg';
import AccountDashboardLoader from './AccountDashboardLoader';
import { ReduxState } from '../../../store/types';
import {useDispatch, useSelector} from 'react-redux';
import { actions } from '../../../store';
import AccountDashboardPlanWidgetCounter from './AccountDashboardPlanWidgetCounter';

const AccountDashboardPlanWidget: React.VFC = () => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {isLoadingProductInfo, accountProductInfo, isLoadingCounters, counters, addonProducts, isLoadingAddon} = useSelector((state: ReduxState) => state.accountDashboard);
    const {accountDetails, isLoadingAccountDetails} = useSelector((state: ReduxState) => state.accountControlPanel);

    const isLoading = useMemo(() => 
        (isLoadingProductInfo || isLoadingAccountDetails || isLoadingCounters || isLoadingAddon) ?? false, 
    [isLoadingProductInfo, isLoadingAccountDetails, isLoadingCounters, isLoadingAddon]);

    useEffect(() => {
        if(accountDetails) {
            dispatch(actions.getAccountProductInfo.request({i_account: accountDetails.i_account}));
            dispatch(actions.getAccountDashboardCounters.request({
                i_account: accountDetails.i_account,
                separate_units: 0,
                with_discount_info: 1,
            }));
            dispatch(actions.getAccountDashboardDiscountPlanList.request({
                get_total: 1,
                limit: 1000,
                offset: 0,
                check_usage: 1,
            }));
        }
    }, [accountDetails]);

    const mappedCounters = useMemo(() => {
        const mapped1 = (counters
            ?.filter(e => e.i_vd_plan === accountProductInfo?.i_vd_plan)
            ?? []);
        return mapped1;
    }, [counters, accountProductInfo]);

    const mappedAddons = useMemo(() => {
        return accountDetails?.assigned_addons?.map(e => {
            return {
                addOnName: e.end_user_name?.length ? e.end_user_name :  e.name,
                counters: counters
                    ?.filter(f => f.i_vd_plan === e.i_vd_plan) ?? []
            };
        });
    }, [counters, accountDetails]);
    
    const mappedCustomBundles = useMemo(() => {
        const filtered = counters
            ?.filter(e => !addonProducts?.find(c => c.i_vd_plan === e.i_vd_plan) 
                && e.i_vd_plan !== accountProductInfo?.i_vd_plan);
        const mapped2 = filtered
            ?.map(e => {
                return {
                    conter: e,
                    addOnName: e.vdp_name ?? e.dg_name
                };
            })
            ?? [];
        const distinctNames = [...new Set(mapped2.map(e => e.addOnName))];
        const retArray = [];
        for(const n of distinctNames) {
            retArray.push({
                addOnName: n,
                counters: mapped2.filter(e => e.addOnName === n)
                    .map(c => c.conter)
            });
        }
        return retArray;
    }, [counters, addonProducts, accountProductInfo]);
    
    return (<AccountDashboardWidgetBase
        route={Routes.AccountMyProfile}
        icon={<Plan />}
        label={t('screens:accountDashboard.plan')}
        customWidgetContentClass={classes.customWidgetContentClass}
        customWidgetContainerClass={classes.customWidgetContainerClass}
    >
        {isLoading && (<AccountDashboardLoader />)}
        {accountProductInfo && !isLoading && 
            (<AccountDashboardPlanWidgetCounter
                counters={mappedCounters}
                productName={accountProductInfo.end_user_name?.length ? accountProductInfo.end_user_name : accountProductInfo.name}
            />)
        }
        {(mappedAddons?.length ?? 0) > 0 && !isLoading && mappedAddons?.map((addOn, indx) =>
            (<AccountDashboardPlanWidgetCounter
                counters={addOn.counters}
                productName={addOn.addOnName}
                key={'addons_' + indx}
                isAddOn
            />)
        )}
        {(mappedCustomBundles?.length ?? 0) > 0 && !isLoading && mappedCustomBundles?.map((addOn, indx) =>
            (<AccountDashboardPlanWidgetCounter
                counters={addOn.counters}
                productName={addOn.addOnName}
                key={'addons_' + indx}
                isShared
            />)
        )}
    </AccountDashboardWidgetBase>
    );
};

export default AccountDashboardPlanWidget;